/*
  Страница должника — §3.8.2, §3.8.3.

  Единственный экран продукта, который видит не сотрудник, а человек с долгом.
  Отсюда всё её устройство: ни входа, ни пароля, ни меню, ни единого термина.
  Задача — чтобы человек, которому стыдно звонить, разобрался сам за минуту.

  Ссылка живёт в части адреса после решётки. Эта часть не отправляется ни на
  сервер в журналы, ни в заголовке перехода на чужие сайты — а значит, ключ от
  персональных данных не утекает туда, куда владелец его не посылал.

  Сам экран только собирает части и перезагружает страницу после действия:
  что показать и какими словами, решает сервер (Глава 8.7).
*/

import { useCallback, useEffect, useState } from 'react'

import { debtorApi } from '../api/client.js'
import { Loading, Notice } from '../components/Common.jsx'
import { Breakdown, Payment, Schedule } from '../components/DebtorDebt.jsx'
import { Exits, Instalment } from '../components/DebtorActions.jsx'
import LowVisionPanel from '../components/LowVisionPanel.jsx'
import useLowVision from '../components/useLowVision.js'
import { date, money } from '../api/format.js'

export const DEBTOR_PATH = '/d'

const NO_LINK = {
  message: 'Ссылка неполная.',
  remedy: 'Откройте её из сообщения целиком - вместе с частью после решётки.',
}

/** Ссылка из части адреса после решётки. */
export function linkFromHash(hash) {
  return (hash || '').replace(/^#/, '').trim()
}

export default function DebtorPage() {
  const [token] = useState(() => linkFromHash(window.location.hash))
  const [api] = useState(() => debtorApi(token))
  const [page, setPage] = useState(null)
  const [error, setError] = useState(null)
  // Версия для слабовидящих (02.10.2026, решение владельца): кнопка есть и на
  // экране отказа - прочитать «ссылка не действует» нужно так же, как сумму.
  const lowVision = useLowVision()

  const load = useCallback(() => {
    setError(null)
    api
      .page()
      .then(setPage)
      .catch((failure) => {
        setPage(null)
        setError(failure)
      })
  }, [api])

  useEffect(() => {
    if (!token) {
      setError(NO_LINK)
      return
    }
    load()
  }, [token, load])

  if (error) {
    return (
      <>
        <LowVisionPanel lowVision={lowVision} />
        <div className="login">
          <div className="login__card">
            <Notice error={error} />
          </div>
        </div>
      </>
    )
  }
  if (!page) return <Loading what="Открываем" />

  /*
    🔴 Узкая колонка по центру, а не рабочая зона кабинета (09.09.2026, решение
    владельца «за основу взять любую оплату»).

    Экран наследовал `work` от кабинета - тысяча четыреста пикселей, прижатых к
    левому краю. Строка расшифровки растягивалась во всю ширину, и название
    услуги оказывалось за тысячу пикселей от своей суммы: чтобы прочитать «за
    отопление - 52 700,00 ₽», приходилось вести глазом через пустоту. Так не
    выглядит ни один счёт и ни одна платёжная страница.

    Здесь читатель - должник, а не оператор: у него один экран, одно решение и
    ни одной таблицы, которую надо сравнивать вширь. Шапка при этом выровнена по
    центру - сумма долга и есть заголовок страницы.
  */
  return (
    <>
      <LowVisionPanel lowVision={lowVision} />
      {/* Нажатие на строку читает её вслух, когда это включено в настройках;
          иначе обработчик ничего не делает. */}
      <main className="work paper" onClick={lowVision.readAloud}>
        <div className="head head--paper">
          <div className="head__title">Задолженность {money(page.breakdown.total)}</div>
          <div className="head__note">
            {page.organisation} · лицевой счёт № {page.account_number} · {page.address}
          </div>
          <div className="muted">на {date(page.breakdown.as_of)}</div>
        </div>

        {page.notices.map((notice) => (
          <Notice key={notice} kind="warn">
            <div className="notice__what">{notice}</div>
          </Notice>
        ))}

        <Breakdown breakdown={page.breakdown} penaltySteps={page.penalty_steps} />
        <Payment payment={page.payment} total={page.breakdown.total} />
        <Schedule schedule={page.schedule} />
        {page.schedule.length ? null : (
          <Instalment offer={page.instalment} api={api} onDone={load} />
        )}
        <Exits api={api} onDone={load} />

        <div className="muted">
          Это страница управляющей организации. Ссылка персональная - не передавайте её
          посторонним.
        </div>
      </main>
    </>
  )
}
